import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCompanySettings, updateCompanySettings, discoverSlackUsers, importSlackUsers } from '../services/adminService';
import { useAppContext } from '../context/AppContext';
import FeatureFlags from '../utils/featureFlags';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    slack_enabled: true,
    slack_team_id: null,
    slack_auto_add_users: false,
    appreciation_keyword: 'kudos',
    company_values: []
  });
  
  const [newValue, setNewValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackTeamName, setSlackTeamName] = useState('');
  const [showSlackImportModal, setShowSlackImportModal] = useState(false);
  const [discoveredUsers, setDiscoveredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [importing, setImporting] = useState(false);
  const { currentUser, setCurrentUser } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Redirect if not admin
    if (currentUser && !['admin', 'admin'].includes(currentUser.role)) {
      navigate('/');
      return;
    }

    // Check for Slack OAuth callback success
    if (searchParams.get('slack_connected') === 'true') {
      const teamName = searchParams.get('team_name');
      setSuccessMessage(`Slack workspace "${teamName}" connected successfully!`);
      setSlackConnected(true);
      setSlackTeamName(teamName);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);

      // Clean up URL parameters
      navigate('/admin/settings', { replace: true });
    }

    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await getCompanySettings();
        setSettings(data);

        // Check if Slack is actually connected by checking slack_team_id
        setSlackConnected(!!data.slack_team_id);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchSettings();
    }
  }, [currentUser, navigate, searchParams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddValue = () => {
    const trimmedValue = newValue.trim();

    // Validate that value only contains letters
    if (!trimmedValue) {
      return;
    }

    if (!/^[a-zA-Z]+$/.test(trimmedValue.replace(/\s/g, ''))) {
      alert('Company values can only contain letters (no numbers or special characters)');
      return;
    }

    // Case-insensitive duplicate check
    const existingValue = settings.company_values.find(
      v => v.toLowerCase() === trimmedValue.toLowerCase()
    );

    if (existingValue) {
      alert('This company value already exists');
      return;
    }

    setSettings({
      ...settings,
      company_values: [...settings.company_values, trimmedValue]
    });
    setNewValue('');
  };

  const handleRemoveValue = (value) => {
    setSettings({
      ...settings,
      company_values: settings.company_values.filter(v => v !== value)
    });
  };

  const handleConnectSlack = () => {
    // Get API base URL from environment or use default
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.apprecio.app';

    // Redirect to Slack OAuth authorize endpoint
    if (currentUser && currentUser.company_id) {
      window.location.href = `${API_BASE_URL}/api/v1/slack/oauth/authorize?company_id=${currentUser.company_id}`;
    }
  };

  const handleDiscoverSlackUsers = async () => {
    try {
      const response = await discoverSlackUsers();
      setDiscoveredUsers(response.discovered_users || []);
      setShowSlackImportModal(true);
    } catch (error) {
      console.error('Error discovering Slack users:', error);
      alert('Failed to discover Slack users. Please make sure Slack is connected.');
    }
  };

  const handleImportSlackUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      setImporting(true);
      await importSlackUsers({ selected_users: selectedUsers });
      setSuccessMessage(`Successfully imported ${selectedUsers.length} users from Slack`);
      setShowSlackImportModal(false);
      setSelectedUsers([]);
      setDiscoveredUsers([]);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error importing Slack users:', error);
      alert(error.response?.data?.detail || 'Failed to import Slack users');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      await updateCompanySettings(settings);

      // Update currentUser context with new company name
      if (currentUser && settings.company_name !== currentUser.company_name) {
        setCurrentUser({ ...currentUser, company_name: settings.company_name });
      }

      setSuccessMessage('Settings saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading settings...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Company Settings</h1>
        <p className="mt-2 text-sm text-gray-600">Configure your company's Apprecio instance.</p>
      </div>
      
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6">
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {successMessage}
            </div>
          )}

          {/* Company Information Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={settings.company_name}
                onChange={handleChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
          </div>

          {/* Slack Integration Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Slack Integration</h3>
            <p className="text-sm text-gray-600 mb-4">
              Connect your Slack workspace to enable appreciation notifications and commands.
            </p>

            <div className="mt-4 flex items-center justify-between bg-gray-50 p-4 rounded-md">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                </svg>
                <div className="ml-3">
                  {slackConnected ? (
                    <>
                      <p className="text-sm font-medium text-gray-900">Connected</p>
                      {slackTeamName && (
                        <p className="text-sm text-gray-500">Workspace: {slackTeamName}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900">Not Connected</p>
                      <p className="text-sm text-gray-500">Connect your Slack workspace to get started</p>
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleConnectSlack}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {slackConnected ? 'Reconnect' : 'Connect'} Slack
              </button>
            </div>

            {/* Custom Appreciation Keyword */}
            {slackConnected && (
              <div className="mt-6">
                <label htmlFor="appreciation_keyword" className="block text-sm font-medium text-gray-700">
                  Custom Appreciation Keyword
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="appreciation_keyword"
                    name="appreciation_keyword"
                    value={settings.appreciation_keyword || ''}
                    onChange={handleChange}
                    pattern="[a-zA-Z]{2,20}"
                    maxLength={20}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="love"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Customize the keyword for Slack appreciations. Default: <code className="bg-gray-100 px-1 rounded">love</code>
                    <br />
                    Example: <code className="bg-gray-100 px-1 rounded">@Apprecio {settings.appreciation_keyword || 'love'} @jane "Great work!"</code>
                    <br />
                    <span className="text-xs text-gray-400">2-20 letters only, no spaces or special characters</span>
                  </p>
                </div>
              </div>
            )}

            {/* Auto-Add Users from Slack */}
            {slackConnected && (
              <div className="mt-6">
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="slack_auto_add_users"
                      name="slack_auto_add_users"
                      type="checkbox"
                      checked={settings.slack_auto_add_users || false}
                      onChange={(e) => setSettings({...settings, slack_auto_add_users: e.target.checked})}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="slack_auto_add_users" className="font-medium text-gray-700">
                      Automatically add users from Slack
                    </label>
                    <p className="text-gray-500 mt-1">
                      ⚠️ When enabled, new users will be automatically created when they interact with the Apprecio bot in Slack. This counts toward your seat limit.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Import Existing Slack Users */}
            {FeatureFlags.SLACK_USER_IMPORT && slackConnected && (
              <div className="mt-6 p-4 bg-blue-50 rounded-md border border-blue-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">One-Time User Import</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Import existing users from your Slack workspace in bulk.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDiscoverSlackUsers}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <i className="fab fa-slack mr-2"></i>
                    Import from Slack
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Company Values Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Values</h3>
            <p className="text-sm text-gray-600 mb-4">
              Define your company's core values that can be tagged in appreciations. Values can only contain letters (e.g., "Innovation", "Teamwork").
            </p>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Add a company value"
                  className="block w-full border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddValue}
                  className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 text-sm font-medium rounded-r-md bg-gray-50 hover:bg-gray-100"
                >
                  Add
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                {settings.company_values.map((value, index) => (
                  <div key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-blue-100 text-blue-800">
                    <span>{value}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveValue(value)}
                      className="ml-1.5 inline-flex text-blue-400 hover:text-blue-600 focus:outline-none"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="mr-3 px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                saving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Slack Import Modal */}
      {FeatureFlags.SLACK_USER_IMPORT && showSlackImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Import Users from Slack</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Found {discoveredUsers.length} users in your Slack workspace. Select users to import:
              </p>

              <div className="max-h-64 overflow-y-auto">
                {discoveredUsers.map((user) => (
                  <label key={user.id} className="flex items-center p-3 hover:bg-gray-50 rounded-md">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-700">
                  {selectedUsers.length} users selected.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-4 rounded-b-lg">
              <button
                onClick={() => {
                  setShowSlackImportModal(false);
                  setSelectedUsers([]);
                  setDiscoveredUsers([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImportSlackUsers}
                disabled={selectedUsers.length === 0 || importing}
                className="px-4 py-2 border border-transparent rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {importing ? 'Importing...' : `Import ${selectedUsers.length} Users`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
